import {
  ApiError,
  activateZone,
  deactivateZone,
  getLocation,
  listZones,
  updateZone,
} from '@jq4race/api-client';
import { LocalizedError, useTranslation } from '@jq4race/i18n';
import type { LocationResponse, UpdateZoneRequest, ZoneResponse } from '@jq4race/shared';
import { Badge, buttonVariants, Card, CardContent, Skeleton } from '@jq4race/ui';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { computeZoneCapacityHint } from '../../lib/zoneCapacityHint';
import { ActivateZoneDialog } from '../components/ActivateZoneDialog';
import { DeactivateZoneDialog } from '../components/DeactivateZoneDialog';
import { ZoneForm } from '../components/ZoneForm';
import { STATUS_TONE } from '../constants';

export function ZoneEditPage() {
  const { t } = useTranslation('zones');
  const { locationId, zoneId } = useParams({ strict: false });
  const navigate = useNavigate();

  const [location, setLocation] = useState<LocationResponse | null>(null);
  const [zones, setZones] = useState<ZoneResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogAction, setDialogAction] = useState<'deactivate' | 'activate' | null>(null);

  // Fetches location + the location's full zone list — the SAME pair
  // ZonesIndexPage already fetches for its own capacity summary — rather
  // than a dedicated getZone call: the zone being edited is found in this
  // list, and the same list feeds computeZoneCapacityHint's exclude-self sum
  // (Arreglo 2). Net effect vs. before: one extra request (was 1: getZone;
  // now 2: getLocation + listZones), traded for the remaining-capacity hint
  // and one fewer distinct endpoint to fetch from.
  const loadData = useCallback(async () => {
    if (!locationId || !zoneId) return;
    setLoading(true);
    setError(null);
    try {
      const [locationRes, zonesRes] = await Promise.all([
        getLocation(locationId),
        listZones(locationId),
      ]);
      setLocation(locationRes);
      setZones(zonesRes.zones);
    } catch (e) {
      setError(translateApiError(e, t, { fallback: t('page.edit.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [locationId, zoneId, t]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const zone = zones.find((z) => z.id === zoneId) ?? null;
  const capacityHint = computeZoneCapacityHint({
    locationCapacity: location?.vehicleCapacity ?? null,
    zones,
    excludeZoneId: zoneId,
  });

  async function handleSubmit(values: UpdateZoneRequest) {
    if (!locationId || !zoneId) return;
    try {
      await updateZone(locationId, zoneId, values);
      void navigate({ to: '/dashboard/locations/$locationId/zones', params: { locationId } });
    } catch (e) {
      if (e instanceof ApiError) {
        throw new LocalizedError(translateApiError(e, t, { fallback: t('page.edit.updateError') }));
      }
      throw e;
    }
  }

  function replaceZone(updated: ZoneResponse) {
    setZones((prev) => prev.map((z) => (z.id === updated.id ? updated : z)));
  }

  async function handleDeactivateConfirm() {
    if (!locationId || !zoneId || !zone) return;
    try {
      const updated = await deactivateZone(locationId, zoneId);
      replaceZone(updated);
    } catch (e) {
      setError(translateApiError(e, t, { fallback: t('feedback.operationFailed') }));
    } finally {
      setDialogAction(null);
    }
  }

  async function handleActivateConfirm() {
    if (!locationId || !zoneId || !zone) return;
    try {
      const updated = await activateZone(locationId, zoneId);
      replaceZone(updated);
    } catch (e) {
      setError(translateApiError(e, t, { fallback: t('feedback.operationFailed') }));
    } finally {
      setDialogAction(null);
    }
  }

  if (loading) {
    return (
      <div className="space-y-8">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !zone) {
    return (
      <div className="rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger">
        {error ?? t('page.edit.notFound')}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.edit.title', { name: zone.name })}
        description={t('page.edit.description')}
        meta={
          <Badge variant={STATUS_TONE[zone.status]} className="capitalize">
            {t(`status.${zone.status}`)}
          </Badge>
        }
        actions={
          <div className="flex items-center gap-2">
            {zone.status === 'active' ? (
              <button
                type="button"
                className={`${buttonVariants({ size: 'sm', variant: 'ghost' })} text-jq-danger hover:text-jq-danger`}
                onClick={() => setDialogAction('deactivate')}
              >
                {t('actions.deactivate')}
              </button>
            ) : (
              <button
                type="button"
                className={buttonVariants({ size: 'sm', variant: 'ghost' })}
                onClick={() => setDialogAction('activate')}
              >
                {t('actions.activate')}
              </button>
            )}
            <Link
              to="/dashboard/locations/$locationId/zones"
              params={{ locationId: locationId ?? '' }}
              className={buttonVariants({ size: 'sm', variant: 'ghost' })}
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
              {t('page.edit.backToZones')}
            </Link>
          </div>
        }
      />

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <ZoneForm
            mode="edit"
            defaultValues={{ name: zone.name, capacity: zone.capacity }}
            onSubmit={handleSubmit}
            submitLabel={t('form.saveChanges')}
            capacityHint={capacityHint}
            onCancel={() =>
              void navigate({
                to: '/dashboard/locations/$locationId/zones',
                params: { locationId: locationId ?? '' },
              })
            }
          />
        </CardContent>
      </Card>

      <DeactivateZoneDialog
        zone={dialogAction === 'deactivate' ? zone : null}
        onConfirm={handleDeactivateConfirm}
        onCancel={() => setDialogAction(null)}
      />

      <ActivateZoneDialog
        zone={dialogAction === 'activate' ? zone : null}
        onConfirm={handleActivateConfirm}
        onCancel={() => setDialogAction(null)}
      />
    </div>
  );
}
